import React, { useContext, useRef, useState, useEffect } from 'react'
import './Navbar.css'
import { Link, useLocation } from 'react-router-dom'
import { ShopContext } from '../../Context/ShopContext'
import logo from '../Assets/logo.webp'
import Search from '../Search/Search'
import PrefetchLink from '../PrefetchLink/PrefetchLink'

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

const getInitials = (name = '') =>
  name.trim()
    ? name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '';

const NAV_LINKS = [
  { label: "SHOP",      to: "/shop",       prefetch: "products" },
  { label: "WHY FORGE", to: "/why-forge"                        },
  { label: "BLOG",      to: "/blog"                            },
  { label: "ABOUT US",  to: "/about-us"                        },
  { label: "CONTACT",   to: "/contact"                         },
  { label: "FAQ",       to: "/faq"                             },
];

const Navbar = () => {
  const location                  = useLocation();
  const [userOpen, setUserOpen]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [userName, setUserName]   = useState(localStorage.getItem('user-name') || '');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('user-email') || '');
  const { getTotalCartItems }     = useContext(ShopContext);
  const userRef    = useRef();
  const isLoggedIn = !!localStorage.getItem('auth-token');
  const initials   = getInitials(userName);

  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (!token) return;
    if (localStorage.getItem('user-name') && localStorage.getItem('user-email')) return;
    fetch(`${API}/me`, { headers: { 'auth-token': token } })
      .then(r => r.json())
      .then(d => {
        if (d.name)  { localStorage.setItem('user-name', d.name);   setUserName(d.name); }
        if (d.email) { localStorage.setItem('user-email', d.email); setUserEmail(d.email); }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = e => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close drawer on resize to desktop
  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Flag the drawer's open state on <body> so fixed overlays (e.g. the sticky
  // buy bar) can hide themselves while it's open, without prop drilling.
  useEffect(() => {
    document.body.classList.toggle('nav-drawer-is-open', mobileOpen);
    return () => document.body.classList.remove('nav-drawer-is-open');
  }, [mobileOpen]);

  // Solid/blurred background once scrolled down. On the mobile hero, stay
  // transparent over the image and only blur once the white card beneath
  // it reaches the nav — everywhere else, a small fixed scroll threshold.
  useEffect(() => {
    const THRESHOLD = 24;
    let rafId = null;
    // Layout reads (offsetHeight / getBoundingClientRect) deferred to the next
    // animation frame instead of running straight in the scroll/resize handler —
    // reading them synchronously on every event forces the browser to flush
    // layout immediately (Lighthouse flags this as "Forced reflow"); rAF also
    // caps this to once per frame during rapid scrolling.
    const measure = () => {
      rafId = null;
      const heroCard = window.innerWidth <= 480
        ? document.querySelector('.hero-content-card')
        : null;
      if (heroCard) {
        const navEl = document.querySelector('.nav');
        const navHeight = navEl ? navEl.offsetHeight : 64;
        setScrolled(heroCard.getBoundingClientRect().top <= navHeight);
      } else {
        setScrolled(window.scrollY > THRESHOLD);
      }
    };
    const handler = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(measure);
    };
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-name');
    localStorage.removeItem('user-email');
    setUserOpen(false);
    window.location.replace('/');
  };

  const CartIcon = () => (
    <div className="nav-cart-wrap">
      <Link to="/cart">
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
      </Link>
      {getTotalCartItems() > 0 && (
        <div className="nav-cart-count">{getTotalCartItems()}</div>
      )}
    </div>
  );

  const UserSection = () => (
    <div className="nav-account-slot">
      {isLoggedIn ? (
        <div className="nav-user-wrap" ref={userRef}>
          <button className="nav-user-btn" onClick={() => setUserOpen(o => !o)}>
            {initials ? (
              <div className="nav-user-avatar">{initials}</div>
            ) : (
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </button>
          {userOpen && (
            <div className="nav-user-dropdown">
              <Link to="/orders"  className="nav-dd-item" onClick={() => setUserOpen(false)}>My Orders</Link>
              <Link to="/account" className="nav-dd-item" onClick={() => setUserOpen(false)}>Account Settings</Link>
              <div className="nav-dd-divider" />
              <button className="nav-dd-item nav-dd-item--danger" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
      ) : (
        <Link to='/login' style={{ textDecoration: 'none' }}>
          <button className="nav-btn">LOGIN</button>
        </Link>
      )}
    </div>
  );

  return (
    <div className={`nav-wrapper${scrolled ? ' nav-wrapper--scrolled' : ''}`}>

      {/* ── Main navbar ── */}
      <nav className="nav">
        {/* Hamburger (mobile) */}
        <button className="nav-hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
          <span className="nav-hamburger-line nav-hamburger-line--1" />
          <span className="nav-hamburger-line nav-hamburger-line--2" />
          <span className="nav-hamburger-line nav-hamburger-line--3" />
        </button>

        {/* Logo */}
        <Link to='/' className="nav-logo">
          <img src={logo} alt="FORGE" className="nav-logo-img" decoding="async" draggable={false} onContextMenu={e => e.preventDefault()} />
        </Link>

        {/* Desktop links */}
        <ul className="nav-menu">
          {NAV_LINKS.map(({ label, to, prefetch }) => {
            const isHash   = to.startsWith('/#');
            const isActive = isHash
              ? location.hash === to.slice(1)
              : location.pathname === to;
            return (
              <li key={label}>
                {prefetch
                  ? <PrefetchLink to={to} prefetch={prefetch} title={label}>{label}</PrefetchLink>
                  : <Link to={to}>{label}</Link>
                }
                {isActive && <hr />}
              </li>
            );
          })}
          {isLoggedIn && (
            <li>
              <Link to='/orders'>MY ORDERS</Link>
              {location.pathname === "/orders" && <hr />}
            </li>
          )}
        </ul>

        {/* Right actions — cart, then search, then account (account hidden on mobile; lives in the drawer instead) */}
        <div className="nav-actions">
          <CartIcon />
          <button className="nav-search-btn" aria-label="Search" onClick={() => setSearchOpen(true)}>
            <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
          <UserSection />
        </div>
      </nav>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div className="nav-drawer-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Search overlay ── */}
      {searchOpen && <Search onClose={() => setSearchOpen(false)} />}

      {/* ── Mobile drawer ── */}
      <div className={`nav-drawer${mobileOpen ? ' nav-drawer--open' : ''}`}>
        <div className="nav-drawer-head">
          {isLoggedIn ? (
            <div className="nav-drawer-user">
              {initials ? (
                <div className="nav-drawer-avatar">{initials}</div>
              ) : (
                <div className="nav-drawer-avatar">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
              )}
              <div className="nav-drawer-user-info">
                <span className="nav-drawer-user-name">{userName || 'Account'}</span>
                {userEmail && <span className="nav-drawer-user-email">{userEmail}</span>}
              </div>
            </div>
          ) : (
            <Link to="/" className="nav-drawer-logo-link" onClick={() => setMobileOpen(false)}>
              <img src={logo} alt="FORGE" className="nav-drawer-logo-img" decoding="async" draggable={false} onContextMenu={e => e.preventDefault()} />
            </Link>
          )}
          <button className="nav-drawer-close" onClick={() => setMobileOpen(false)}>✕</button>
        </div>
        <ul className="nav-drawer-links">
          {NAV_LINKS.map(({ label, to }) => {
            const isHash   = to.startsWith('/#');
            const isActive = isHash ? location.hash === to.slice(1) : location.pathname === to;
            return (
              <li key={label}>
                <Link to={to} className={isActive ? 'nav-drawer-link--active' : ''} onClick={() => setMobileOpen(false)}>{label}</Link>
              </li>
            );
          })}
          {isLoggedIn && (
            <>
              <li><Link to='/orders' className={location.pathname === '/orders' ? 'nav-drawer-link--active' : ''} onClick={() => setMobileOpen(false)}>MY ORDERS</Link></li>
              <li><Link to='/account' className={location.pathname === '/account' ? 'nav-drawer-link--active' : ''} onClick={() => setMobileOpen(false)}>ACCOUNT SETTINGS</Link></li>
            </>
          )}
        </ul>
        {isLoggedIn ? (
          <button className="nav-drawer-logout" onClick={handleLogout}>LOGOUT</button>
        ) : (
          <Link to='/login' className="nav-drawer-login" onClick={() => setMobileOpen(false)}>LOGIN / SIGNUP</Link>
        )}
      </div>

    </div>
  );
};

export default Navbar;
